import logger from '../utils/logger';
/**
 * DATA MANAGER - Orchestrateur Central
 * 
 * Architecture inspirée de Notion, Linear, ChatGPT
 * - Cache-first (IndexedDB + Memory)
 * - Optimistic updates
 * - Realtime sync
 * - Offline-first avec queue
 * 
 * Principe: L'UI NE BLOQUE JAMAIS
 */

import { supabase } from './supabase';
import { PersistentCache } from './persistent-cache';
import { API_URL } from './config';

// ============================================
// TYPES
// ============================================

interface OptimisticUpdate<T> {
 id: string;
 type: 'insert' | 'update' | 'delete';
 table: string;
 data: T;
 timestamp: number;
 status: 'pending' | 'success' | 'failed';
 retry: number;
}

interface SyncQueueItem {
 id: string;
 action: () => Promise<any>;
 rollback: () => void;
 retry: number;
 maxRetry: number;
}

// ============================================
// STATE MANAGEMENT
// ============================================

class DataManagerClass {
 private optimisticUpdates: Map<string, OptimisticUpdate<any>> = new Map();
 private syncQueue: SyncQueueItem[] = [];
 private isOnline: boolean = navigator.onLine;
 private memoryCache: Map<string, { data: any; timestamp: number }> = new Map();
 private readonly MEMORY_CACHE_TTL = 60000; // 1 minute

 constructor() {
 // Setup online/offline listeners
 window.addEventListener('online', () => {
 logger.log(' [DataManager] Back online, processing queue...');
 this.isOnline = true;
 this.processQueue();
 });

 window.addEventListener('offline', () => {
 logger.warn(' [DataManager] Offline mode activated');
 this.isOnline = false;
 });

 logger.log(' [DataManager] Initialized');
 }

 // ============================================
 // PROFILE MANAGEMENT
 // ============================================

 /**
 * Get profile with cache-first strategy
 * 1. Memory cache (< 10ms)
 * 2. IndexedDB (< 50ms)
 * 3. Supabase (background)
 */
 async getProfile(userId: string): Promise<any | null> {
 const startTime = Date.now();

 // 1. Memory cache
 const memCached = this.getFromMemory(`profile:${userId}`);
 if (memCached) {
 logger.log(` [DataManager] Profile from memory (${Date.now() - startTime}ms)`);
 
 // Refresh in background
 this.refreshProfileBackground(userId);
 
 return memCached;
 }

 // 2. IndexedDB
 try {
 const cached = await PersistentCache.getProfile(userId);
 if (cached) {
 logger.log(` [DataManager] Profile from IndexedDB (${Date.now() - startTime}ms)`);
 
 // Save to memory
 this.saveToMemory(`profile:${userId}`, cached);
 
 // Refresh in background
 this.refreshProfileBackground(userId);
 
 return cached;
 }
 } catch (error) {
 logger.error(' [DataManager] IndexedDB error:', error);
 }

  // 3. Supabase (no cache) - with 5s timeout + AbortController
  logger.log(' [DataManager] Fetching profile from Supabase...');
  
  return new Promise<any>(async (resolve) => {
    let resolved = false;
    
    // TIMEOUT ABSOLU: Si après 5s rien ne se passe, on retourne null
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        logger.warn(' [DataManager] Profile fetch TIMEOUT (5s) - force resolve null');
        resolve(null);
      }
    }, 5000);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      // Clear timeout si la requête répond
      clearTimeout(timeoutId);
      
      if (!resolved) {
        resolved = true;
        
        if (error) {
          logger.warn(' [DataManager] Profile fetch error:', error.message);
          resolve(null);
          return;
        }
        
        if (data) {
          logger.log(` [DataManager] Profile from Supabase (${Date.now() - startTime}ms)`);
          
          // Save to caches (non-blocking)
          this.saveToMemory(`profile:${userId}`, data);
          PersistentCache.saveProfile(data).catch(err => {
            logger.warn(' [DataManager] Failed to cache profile:', err);
          });
          
          resolve(data);
        } else {
          logger.warn(' [DataManager] No profile data returned');
          resolve(null);
        }
      }
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (!resolved) {
        resolved = true;
        logger.warn(' [DataManager] Profile fetch exception:', error.message);
        resolve(null);
      }
    }
  });
 }

 /**
 * Refresh profile in background (non-blocking)
 */
 private async refreshProfileBackground(userId: string): Promise<void> {
 try {
 const { data, error } = await supabase
 .from('profiles')
 .select('*')
 .eq('id', userId)
 .single();

 if (error) throw error;

 if (data) {
 // Check if different from cache
 const cached = await PersistentCache.getProfile(userId);
 
 if (JSON.stringify(data) !== JSON.stringify(cached)) {
 logger.log(' [DataManager] Profile changed, updating cache');
 this.saveToMemory(`profile:${userId}`, data);
 await PersistentCache.saveProfile(data);
 
 // Notify subscribers (TODO: implement event system)
 this.notifyChange('profile', userId, data);
 }
 }
 } catch (error) {
 logger.warn(' [DataManager] Background refresh failed (non-blocking):', error);
 }
 }

 /**
 * Update profile with optimistic update
 */
 async updateProfile(userId: string, updates: Partial<any>): Promise<void> {
 logger.log(' [DataManager] Optimistic profile update');

 // 1. Get current data
 const current = await this.getProfile(userId);
 if (!current) throw new Error('Profile not found');

 // 2. Apply update optimistically
 const optimistic = { ...current, ...updates, updated_at: new Date().toISOString() };
 
 // 3. Update caches immediately
 this.saveToMemory(`profile:${userId}`, optimistic);
 await PersistentCache.saveProfile(optimistic);
 
 // 4. Notify UI
 this.notifyChange('profile', userId, optimistic);

 // 5. Queue API call
 this.queueSync({
 id: `profile-update-${Date.now()}`,
 action: async () => {
 const { error } = await supabase
 .from('profiles')
 .update(updates)
 .eq('id', userId);

 if (error) throw error;
 },
 rollback: () => {
 // Rollback to previous state
 this.saveToMemory(`profile:${userId}`, current);
 PersistentCache.saveProfile(current);
 this.notifyChange('profile', userId, current);
 },
 retry: 0,
 maxRetry: 3,
 });
 }

 // ============================================
 // SESSIONS MANAGEMENT
 // ============================================

 /**
 * Get sessions with cache
 */
 async getSessions(userId: string, forceRefresh = false): Promise<any[]> {
    // 1. Try IndexedDB - PROTECTION contre cache corrompu (sauf si forceRefresh)
    if (!forceRefresh) {
      try {
        const cached = await PersistentCache.getSessions(userId);
        if (cached.length > 0) {
          logger.log(` [DataManager] Sessions from cache (${cached.length})`);
          
          // Refresh in background
          this.refreshSessionsBackground(userId);
          
          return cached;
        }
      } catch (cacheError) {
        logger.warn(' [DataManager] Sessions cache error (non-blocking):', cacheError);
        // Continuer vers API
      }
    }

 // 2. Fetch from API (avec stats: insights_count, reports_count, etc.)
 logger.log(' [DataManager] Fetching sessions from API with stats...');
 
 try {
   const response = await fetch(`${API_URL}/api/chat/sessions-with-stats/${userId}`, {
     headers: {
       'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
     }
   });

   if (!response.ok) {
     throw new Error(`HTTP ${response.status}`);
   }

   const { sessions } = await response.json();

   if (sessions && sessions.length > 0) {
     logger.log(` [DataManager] Sessions from API (${sessions.length})`);
     await PersistentCache.saveSessions(sessions);
     return sessions;
   }

   return [];
 } catch (error) {
   logger.error(' [DataManager] Error fetching sessions from API:', error);
   
   // Fallback: Essayer Supabase direct (sans stats)
   try {
     const { data, error: supabaseError } = await supabase
       .from('chat_sessions')
       .select('*')
       .eq('user_id', userId)
       .order('created_at', { ascending: false });

     if (supabaseError) throw supabaseError;

     if (data) {
       logger.log(` [DataManager] Sessions from Supabase fallback (${data.length})`);
       // Ajouter compteurs vides pour compatibilité
       const sessionsWithEmptyStats = data.map(s => ({
         ...s,
         insights_count: 0,
         reports_count: 0,
         charts_count: 0,
         has_deep_report: false
       }));
       await PersistentCache.saveSessions(sessionsWithEmptyStats);
       return sessionsWithEmptyStats;
     }

     return [];
   } catch (fallbackError) {
     logger.error(' [DataManager] Supabase fallback also failed:', fallbackError);
     return [];
   }
 }
 }

  /**
  * Refresh sessions in background
  */
  private async refreshSessionsBackground(userId: string): Promise<void> {
    try {
      // Utiliser l'API pour avoir les stats
      const response = await fetch(`${API_URL}/api/chat/sessions-with-stats/${userId}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const { sessions } = await response.json();

      if (sessions) {
        const cached = await PersistentCache.getSessions(userId);
        
        if (JSON.stringify(sessions) !== JSON.stringify(cached)) {
          logger.log(' [DataManager] Sessions changed, updating cache');
          await PersistentCache.saveSessions(sessions);
          this.notifyChange('sessions', userId, sessions);
        }
      }
    } catch (error) {
      logger.warn(' [DataManager] Background sessions refresh failed:', error);
    }
  }

  // ============================================
  // INSIGHTS MANAGEMENT
  // ============================================

  /**
   * Get complete session data (insights + reports + dataViz) - OPTIMIZED
   * Utilisé par Studio pour restauration de session
   */
  async getSessionComplete(sessionId: string, userId: string): Promise<any> {
    const cacheKey = `session_complete:${sessionId}`;
    const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

    // 1. Memory cache
    const memCached = this.getFromMemory(cacheKey);
    if (memCached) {
      const age = Date.now() - memCached.timestamp;
      if (age < CACHE_TTL) {
        logger.log(` [DataManager] Session complete from memory (${age}ms)`);
        
        // Refresh en background si > 30s
        if (age > 30000) {
          this.refreshSessionCompleteBackground(sessionId, userId);
        }
        
        return memCached.data;
      }
    }

    // 2. Fetch depuis API (endpoint optimisé avec JOIN SQL)
    logger.log(' [DataManager] Fetching session complete from API...');
    
    try {
      const response = await fetch(`${API_URL}/api/session/${sessionId}/complete`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      // Save to memory cache
      this.saveToMemory(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      logger.error(' [DataManager] Error fetching session complete:', error);
      return {
        insights: [],
        reports: [],
        dataViz: [],
        stats: {},
        session: null
      };
    }
  }

  /**
   * Refresh session complete in background
   */
  private async refreshSessionCompleteBackground(sessionId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/api/session/${sessionId}/complete`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      // Update memory cache
      this.saveToMemory(`session_complete:${sessionId}`, {
        data: result,
        timestamp: Date.now()
      });

      // Notify change
      this.notifyChange('session_complete', sessionId, result);
    } catch (error) {
      logger.warn(' [DataManager] Background session complete refresh failed:', error);
    }
  }

  async getInsights(userId: string): Promise<any[]> {
    // PROTECTION: Si cache corrompu, continuer sans bloquer
    let cached: any[] = [];
    try {
      cached = await PersistentCache.getInsightsByUser(userId);
      if (cached.length > 0) {
        logger.log(` [DataManager] Insights from cache (${cached.length})`);
        this.refreshInsightsBackground(userId);
        return cached;
      }
    } catch (cacheError) {
      logger.warn(' [DataManager] Cache error (non-blocking):', cacheError);
      // Continuer vers Supabase même si cache fail
    }

    try {
      // 1. Get general insights
      const { data: generalInsights, error: error1 } = await supabase
        .from('insights')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error1) throw error1;

      // 2. Get chat insights (via JOIN with chat_sessions)
      const { data: chatInsights, error: error2 } = await supabase
        .from('chat_insights')
        .select(`
          *,
          chat_sessions!inner(user_id)
        `)
        .eq('chat_sessions.user_id', userId)
        .order('created_at', { ascending: false });

      if (error2) {
        logger.warn(' [DataManager] Error fetching chat_insights (non-blocking):', error2);
      }

      // 3. Merge both sources
      const allInsights = [
        ...(generalInsights || []),
        ...(chatInsights || []).map((ci: any) => ({
          ...ci,
          user_id: userId, // Add user_id for caching
          source: 'chat', // Mark as chat insight
        }))
      ];

      logger.log(` [DataManager] Insights loaded: ${generalInsights?.length || 0} general + ${chatInsights?.length || 0} chat`);

      if (allInsights.length > 0) {
        await PersistentCache.saveInsights(allInsights);
      }

      return allInsights;
    } catch (error) {
      logger.error(' [DataManager] Error fetching insights:', error);
      return [];
    }
  }

  private async refreshInsightsBackground(userId: string): Promise<void> {
    try {
      // Same logic as getInsights but without cache check
      const { data: generalInsights } = await supabase
        .from('insights')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      const { data: chatInsights } = await supabase
        .from('chat_insights')
        .select(`
          *,
          chat_sessions!inner(user_id)
        `)
        .eq('chat_sessions.user_id', userId)
        .order('created_at', { ascending: false });

      const allInsights = [
        ...(generalInsights || []),
        ...(chatInsights || []).map((ci: any) => ({
          ...ci,
          user_id: userId,
          source: 'chat',
        }))
      ];

      if (allInsights.length > 0) {
        await PersistentCache.saveInsights(allInsights);
        this.notifyChange('insights', userId, allInsights);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background insights refresh failed:', error);
    }
  }

  // ============================================
  // DEEP REPORTS MANAGEMENT
  // ============================================

  async getDeepReports(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getDeepReports(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Deep reports from cache (${cached.length})`);
      this.refreshDeepReportsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('deep_reports')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveDeepReports(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching deep reports:', error);
      return [];
    }
  }

  private async refreshDeepReportsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('deep_reports')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveDeepReports(data);
        this.notifyChange('deep_reports', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background deep reports refresh failed:', error);
    }
  }

  // ============================================
  // BUSINESS PATTERNS MANAGEMENT
  // ============================================

  async getBusinessPatterns(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getBusinessPatterns(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Business patterns from cache (${cached.length})`);
      this.refreshBusinessPatternsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('business_patterns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveBusinessPatterns(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching business patterns:', error);
      return [];
    }
  }

  private async refreshBusinessPatternsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('business_patterns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveBusinessPatterns(data);
        this.notifyChange('business_patterns', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background business patterns refresh failed:', error);
    }
  }

  // ============================================
  // PATTERN COLLISIONS MANAGEMENT
  // ============================================

  async getPatternCollisions(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getPatternCollisions(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Pattern collisions from cache (${cached.length})`);
      this.refreshPatternCollisionsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('pattern_collisions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.savePatternCollisions(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching pattern collisions:', error);
      return [];
    }
  }

  private async refreshPatternCollisionsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('pattern_collisions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.savePatternCollisions(data);
        this.notifyChange('pattern_collisions', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background pattern collisions refresh failed:', error);
    }
  }

  // ============================================
  // BUSINESS MATURITY SCORES MANAGEMENT
  // ============================================

  async getBusinessMaturityScores(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getBusinessMaturityScores(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Maturity scores from cache (${cached.length})`);
      this.refreshMaturityScoresBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('business_maturity_scores')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveBusinessMaturityScores(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching maturity scores:', error);
      return [];
    }
  }

  private async refreshMaturityScoresBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('business_maturity_scores')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveBusinessMaturityScores(data);
        this.notifyChange('business_maturity_scores', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background maturity scores refresh failed:', error);
    }
  }

  // ============================================
  // TOKEN TRANSACTIONS MANAGEMENT
  // ============================================

  async getTokenTransactions(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getTokenTransactions(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Token transactions from cache (${cached.length})`);
      this.refreshTokenTransactionsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100); // Limiter aux 100 dernières

      if (error) throw error;
      if (data) await PersistentCache.saveTokenTransactions(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching token transactions:', error);
      return [];
    }
  }

  private async refreshTokenTransactionsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      if (data) {
        await PersistentCache.saveTokenTransactions(data);
        this.notifyChange('token_transactions', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background token transactions refresh failed:', error);
    }
  }

  // ============================================
  // API KEYS MANAGEMENT
  // ============================================

  async getApiKeys(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getApiKeys(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] API keys from cache (${cached.length})`);
      this.refreshApiKeysBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveApiKeys(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching API keys:', error);
      return [];
    }
  }

  private async refreshApiKeysBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveApiKeys(data);
        this.notifyChange('api_keys', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background API keys refresh failed:', error);
    }
  }

  // ============================================
  // SUBSCRIPTIONS MANAGEMENT
  // ============================================

  async getSubscriptions(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getSubscriptions(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Subscriptions from cache (${cached.length})`);
      this.refreshSubscriptionsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveSubscriptions(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching subscriptions:', error);
      return [];
    }
  }

  private async refreshSubscriptionsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveSubscriptions(data);
        this.notifyChange('subscriptions', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background subscriptions refresh failed:', error);
    }
  }

  // ============================================
  // REFERRALS MANAGEMENT
  // ============================================

  async getReferrals(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getReferralsByReferrer(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Referrals from cache (${cached.length})`);
      this.refreshReferralsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveReferrals(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching referrals:', error);
      return [];
    }
  }

  private async refreshReferralsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveReferrals(data);
        this.notifyChange('referrals', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background referrals refresh failed:', error);
    }
  }

  // ============================================
  // RAW DATA MANAGEMENT
  // ============================================

  async getRawData(sessionId: string): Promise<any[]> {
    const cached = await PersistentCache.getRawData(sessionId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Raw data from cache (${cached.length})`);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('raw_data')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (data) await PersistentCache.saveRawData(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching raw data:', error);
      return [];
    }
  }

  // ============================================
  // DASHBOARDS MANAGEMENT
  // ============================================

  async getDashboards(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getDashboards(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] Dashboards from cache (${cached.length})`);
      this.refreshDashboardsBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) await PersistentCache.saveDashboards(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching dashboards:', error);
      return [];
    }
  }

  private async refreshDashboardsBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveDashboards(data);
        this.notifyChange('dashboards', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background dashboards refresh failed:', error);
    }
  }

  // ============================================
  // USER FILES MANAGEMENT
  // ============================================

  async getUserFiles(userId: string): Promise<any[]> {
    const cached = await PersistentCache.getUserFiles(userId);
    if (cached.length > 0) {
      logger.log(` [DataManager] User files from cache (${cached.length})`);
      this.refreshUserFilesBackground(userId);
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from('user_files')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false});

      if (error) throw error;
      if (data) await PersistentCache.saveUserFiles(data);
      return data || [];
    } catch (error) {
      logger.error(' [DataManager] Error fetching user files:', error);
      return [];
    }
  }

  private async refreshUserFilesBackground(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('user_files')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        await PersistentCache.saveUserFiles(data);
        this.notifyChange('user_files', userId, data);
      }
    } catch (error) {
      logger.warn(' [DataManager] Background user files refresh failed:', error);
    }
  }

  // ============================================
  // QUEUE & SYNC
  // ============================================

 /**
 * Queue sync operation (offline-first)
 */
 private queueSync(item: SyncQueueItem): void {
 this.syncQueue.push(item);
 
 if (this.isOnline) {
 this.processQueue();
 } else {
 logger.warn(' [DataManager] Queued for later (offline)');
 }
 }

 /**
 * Process sync queue
 */
 private async processQueue(): Promise<void> {
 if (this.syncQueue.length === 0) return;

 logger.log(` [DataManager] Processing queue (${this.syncQueue.length} items)`);

 const queue = [...this.syncQueue];
 this.syncQueue = [];

 for (const item of queue) {
 try {
 await item.action();
 logger.log(` [DataManager] Synced: ${item.id}`);
 } catch (error) {
 logger.error(` [DataManager] Sync failed: ${item.id}`, error);
 
 if (item.retry < item.maxRetry) {
 item.retry++;
 this.syncQueue.push(item);
 logger.log(` [DataManager] Retry ${item.retry}/${item.maxRetry}: ${item.id}`);
 } else {
 logger.error(` [DataManager] Max retries reached: ${item.id}`);
 item.rollback();
 }
 }
 }
 }

 // ============================================
 // MEMORY CACHE
 // ============================================

 private saveToMemory(key: string, data: any): void {
 this.memoryCache.set(key, {
 data,
 timestamp: Date.now(),
 });
 }

 private getFromMemory(key: string): any | null {
 const cached = this.memoryCache.get(key);
 if (!cached) return null;

 // Check TTL
 if (Date.now() - cached.timestamp > this.MEMORY_CACHE_TTL) {
 this.memoryCache.delete(key);
 return null;
 }

 return cached.data;
 }

 // ============================================
 // NOTIFICATIONS
 // ============================================

 private listeners: Map<string, Set<(data: any) => void>> = new Map();

 /**
 * Subscribe to changes
 */
 subscribe(type: string, callback: (data: any) => void): () => void {
 if (!this.listeners.has(type)) {
 this.listeners.set(type, new Set());
 }

 this.listeners.get(type)!.add(callback);

 return () => {
 this.listeners.get(type)?.delete(callback);
 };
 }

 /**
 * Notify subscribers of changes
 */
 private notifyChange(type: string, id: string, data: any): void {
 const key = `${type}:${id}`;
 this.listeners.get(key)?.forEach(callback => callback(data));
 this.listeners.get(type)?.forEach(callback => callback(data));
 }

 // ============================================
 // CLEANUP
 // ============================================

 async clearAll(): Promise<void> {
 this.memoryCache.clear();
 await PersistentCache.clearAll();
 logger.log(' [DataManager] All caches cleared');
 }
}

// Singleton instance
export const DataManager = new DataManagerClass();

